import React from "react";
import type { SceneCtx, SceneDef } from "./scenes";
import type { Box } from "./frame";
import { Kicker, Headline, Caption } from "./prims";
import { renderVisual, renderPhasedVisual } from "./visuals";
import { renderBoardBeat } from "./board";
import { DrawOnGroup } from "./doodle";
import type { Beat } from "./director";

/** A beat's visual: choreographed phases when present, single actor otherwise.
 *  On the "doodle" theme every visual is HAND-SKETCHED onto the whiteboard —
 *  a marker hand rides a zigzag reveal (doodle.tsx), then lifts away. */
const beatVisual = (beat: Beat, box: Box, theme: any, unit: number, t: number) => {
  const inner = beat.visual.phases && beat.visual.phases.length >= 2
    ? renderPhasedVisual(beat.visual.phases, box, theme, unit, t, beat.dur)
    : renderVisual(beat.visual.kind, beat.visual.items, box, theme, unit, t, beat.visual.assetUri);
  if (theme.id === "doodle") {
    return <DrawOnGroup box={box} t={t} idSeed={beat.id}>{inner}</DrawOnGroup>;
  }
  return inner;
};

const subBox = (x: number, y: number, w: number, h: number): Box => ({ x, y, w, h, cx: x + w / 2, cy: y + h / 2 });

/**
 * Anivaze v4 — data-driven beat templates.
 *
 * A beat template LAYS OUT a `Beat` (title / role / captions) against the frame
 * layout + theme — "templates that design things" (PLAN §8). These are generic
 * and content-agnostic, so ANY script the Director produces renders on-design and
 * deterministically. This is what makes the output usable instead of random.
 */

export interface BeatMeta {
  index: number;
  total: number;
  format: string;
  /**
   * Optional small label above the hook/recap headline. We deliberately do NOT
   * default this to the format word ("explainer") — that read as a stray debug
   * mark on every video. Left empty it renders nothing; the brand kit
   * (PRODUCT-CHAT-EDIT §5) can later feed a topic/brand kicker here.
   */
  kicker?: string;
}

/**
 * Wrap a title into ≤ maxLines lines without measuring glyphs. Never drops
 * words: once the line budget is spent, remaining words pack onto the last line
 * (a slightly long line beats a truncated headline).
 */
export function wrapTitle(title: string, maxChars: number, maxLines = 2): string[] {
  const words = title.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [title];
  const lines: string[] = [];
  let cur = "";
  for (const w of words) {
    if (cur && (cur + " " + w).length > maxChars && lines.length < maxLines - 1) {
      lines.push(cur);
      cur = w;
    } else {
      cur = cur ? cur + " " + w : w;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

const Rule: React.FC<{ cx: number; y: number; w: number; color: string; h?: number }> = ({ cx, y, w, color, h = 6 }) => (
  <rect x={cx - w / 2} y={y} width={w} height={h} rx={h / 2} fill={color} />
);

/** Render a beat into SVG content (background is drawn by the Stage). */
export function renderBeat(beat: Beat, ctx: SceneCtx, meta: BeatMeta): React.ReactNode {
  const { layout, theme, t } = ctx;

  // Teacher pack: the Chalkboard theme swaps every beat to the live-writing
  // board renderer (Khan-style) — content is written, not slid in.
  if (theme.id === "chalk") {
    return renderBoardBeat(beat, { layout, theme, t }, { index: meta.index, total: meta.total });
  }

  const { content, caption, unit } = layout;
  const caps = (
    <>
      <Caption box={caption} segs={beat.captions} t={t} theme={theme} unit={unit} />
      {beat.visual.credit && (
        <text x={layout.full.x + unit * 1.4} y={layout.full.y + layout.full.h - unit * 0.9}
          fontFamily={theme.body.family} fontSize={unit * 1.3} fill={theme.palette.muted} opacity={0.8}>
          📷 {beat.visual.credit}
        </text>
      )}
    </>
  );

  switch (beat.template) {
    // ── Hook / recap: kicker + big statement — WITH the visual when the beat
    //    has one (a recap must re-show the story, not just restate it) ──────
    case "title": {
      const hasVisual = beat.visual.kind !== "none" || (beat.visual.phases?.length ?? 0) >= 2;
      const size = hasVisual ? 6.2 : 8.4;
      const lines = wrapTitle(beat.title, hasVisual ? 20 : 15);
      const lh = unit * size * 1.04;
      const yTop = hasVisual ? content.y + unit * 3.4 : content.cy - (lines.length * lh) / 2;
      const vTop = yTop + lines.length * lh + unit * 1.5;
      const vbox = subBox(content.x, vTop, content.w, Math.max(unit * 18, content.y + content.h - vTop));
      return (
        <>
          {meta.kicker ? <Kicker text={meta.kicker} x={content.cx} y={content.y + unit * 1.6} theme={theme} unit={unit} /> : null}
          <Headline lines={lines} x={content.cx} yTop={yTop} sizeUnit={size} theme={theme} unit={unit} t={t} />
          {hasVisual && beatVisual(beat, vbox, theme, unit, t)}
          {caps}
        </>
      );
    }

    // ── Body point: SMALL heading + the animated concept visual as hero ────
    case "point": {
      const titleLines = wrapTitle(beat.title, 24, 2);
      const tSize = 4.4;
      const tlh = unit * tSize * 1.05;
      const titleTop = content.y + unit * 2;
      const titleBottom = titleTop + titleLines.length * tlh;
      const vy = titleBottom + unit * 1.5;
      const vbox = subBox(content.x, vy, content.w, Math.max(unit * 20, content.y + content.h - vy));
      return (
        <>
          <Headline lines={titleLines} x={content.cx} yTop={titleTop} sizeUnit={tSize} theme={theme} unit={unit} t={t} />
          <Rule cx={content.cx} y={titleBottom + unit * 1.1} w={unit * 8} color={theme.palette.primary} />
          {beatVisual(beat, vbox, theme, unit, t)}
          {caps}
        </>
      );
    }

    // ── Quote: oversized centred line + quote mark ────────────────────────
    case "quote": {
      const lines = wrapTitle(beat.title, 13);
      const size = 9.2;
      const lh = unit * size * 1.03;
      const yTop = content.cy - (lines.length * lh) / 2;
      return (
        <>
          <text
            x={content.cx}
            y={content.y + unit * 3}
            textAnchor="middle"
            fontFamily={theme.display.family}
            fontWeight={theme.display.weight}
            fontSize={unit * 12}
            fill={theme.palette.primary}
            opacity={0.5}
          >
            &ldquo;
          </text>
          <Headline lines={lines} x={content.cx} yTop={yTop} sizeUnit={size} theme={theme} unit={unit} t={t} />
          {caps}
        </>
      );
    }

    // ── CTA: statement + accent pill ──────────────────────────────────────
    case "cta":
    default: {
      const lines = wrapTitle(beat.title, 14);
      const size = 8;
      const lh = unit * size * 1.04;
      const yTop = content.cy - (lines.length * lh) / 2;
      const pillW = unit * 26;
      const pillY = yTop + lines.length * lh + unit * 1.4;
      return (
        <>
          <Headline lines={lines} x={content.cx} yTop={yTop} sizeUnit={size} theme={theme} unit={unit} t={t} />
          <rect x={content.cx - pillW / 2} y={pillY} width={pillW} height={unit * 4.6} rx={unit * 2.3} fill={theme.palette.primary} />
          <text
            x={content.cx}
            y={pillY + unit * 2.3}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={theme.body.family}
            fontWeight={700}
            fontSize={unit * 2.2}
            fill={theme.dark ? "#12151a" : "#ffffff"}
            style={{ textTransform: "uppercase", letterSpacing: `${unit * 0.1}px` }}
          >
            Follow ➜
          </text>
          {caps}
        </>
      );
    }
  }
}

/** Wrap a beat as a SceneDef so the existing Stage can render it unchanged. */
export function beatToScene(beat: Beat, meta: BeatMeta): SceneDef {
  return {
    id: beat.id,
    label: beat.role,
    format: meta.format,
    dur: beat.dur,
    render: (ctx: SceneCtx) => renderBeat(beat, ctx, meta),
  };
}
